import { EditorDoc, Vec3, Corner, NoGoZone } from '@roomtreatment/core';

export interface Point2D {
  x: number;
  y: number;
}

export interface Rect2D {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const DEFAULT_SNAP_STEP = 0.05; // 5 cm grid snap step

/** Snap a value to the nearest grid step */
export function snapToGrid(value: number, step: number = DEFAULT_SNAP_STEP): number {
  return Math.round(value / step) * step;
}

/** Check if a point is inside a rectangular bounding box */
export function isPointInRect(pt: Point2D, rect: Rect2D): boolean {
  return (
    pt.x >= rect.x &&
    pt.x <= rect.x + rect.w &&
    pt.y >= rect.y &&
    pt.y <= rect.y + rect.h
  );
}

/** Check if a position violates any No-Go zone constraints */
export function isInsideNoGoZone(pos: Vec3, zones: NoGoZone[]): boolean {
  const [x, , z] = pos;
  for (const zone of zones) {
    if (x >= zone.x0 && x <= zone.x1 && z >= zone.y0 && z <= zone.y1) {
      return true;
    }
  }
  return false;
}

/** Hit test objects in the 2D floorplan canvas */
export function hitTest2D(
  doc: EditorDoc,
  clickPt: Point2D,
  hitRadius: number = 0.35
): { id: string; kind: 'speaker' | 'listener' | 'panel' | 'furniture' } | null {
  // 1. Listener seat hit test
  if (doc.listener) {
    const dist = Math.hypot(clickPt.x - doc.listener.position[0], clickPt.y - doc.listener.position[2]);
    if (dist <= hitRadius) {
      return { id: doc.listener.id, kind: 'listener' };
    }
  }

  // 2. Speaker hit test
  for (const speaker of doc.speakers) {
    const dist = Math.hypot(clickPt.x - speaker.position[0], clickPt.y - speaker.position[2]);
    if (dist <= hitRadius) {
      return { id: speaker.id, kind: 'speaker' };
    }
  }

  // 3. Panel hit test
  for (const panel of doc.panels) {
    const dist = Math.hypot(clickPt.x - panel.position[0], clickPt.y - panel.position[2]);
    if (dist <= hitRadius) {
      return { id: panel.id, kind: 'panel' };
    }
  }

  // 4. Furniture hit test
  for (const item of doc.furniture) {
    const dist = Math.hypot(clickPt.x - item.position[0], clickPt.y - item.position[2]);
    if (dist <= hitRadius) {
      return { id: item.id, kind: 'furniture' };
    }
  }

  return null;
}

/** Compute footprint polygon vertices from room dimensions or footprint polygon */
export function getRoomPolygonVertices(doc: EditorDoc): Corner[] {
  if (doc.footprint && doc.footprint.length >= 3) {
    return doc.footprint;
  }
  // Rectangular room polygon (CCW)
  const { Lx, Ly } = doc.dims;
  return [
    { x: 0, y: 0, label: 'front' },
    { x: Lx, y: 0, label: 'right' },
    { x: Lx, y: Ly, label: 'back' },
    { x: 0, y: Ly, label: 'left' },
  ];
}

export {
  type PolyPt,
  wingsToPolygon,
  longestWallLength,
  polyBounds,
} from './polygon';

export {
  MIN_FLOOR_NODES,
  MAX_FLOOR_EXTENT_M,
  MIN_WALL_LENGTH_M,
  NODE_SNAP_RADIUS_M,
  ALIGN_SNAP_M,
  hitTestFloorNode,
  hitTestFloorEdge,
  wouldSelfIntersectOnMove,
  polygonSelfIntersects,
  moveFloorNode,
  insertFloorNode,
  insertFloorNodeAt,
  removeFloorNode,
  floorEdgeAxis,
  slideFloorEdge,
  snapEdgeSlide,
  type FloorEdgeAxis,
  viewWorldBounds,
  clampDragPoint,
  zoomViewAt,
  panView,
  MIN_VIEW_SCALE,
  MAX_VIEW_SCALE,
  snapWallAngle,
  snapAxisAlign,
  snapOntoNode,
  snapNextWallPoint,
  snapDragPoint,
  presetFootprint,
  FLOOR_PRESETS,
  type FloorPreset,
  reconcileFootprintDims,
  floorPolygonView,
  computeEdgeDimensions,
  setEdgeLength,
  scaleFootprintAxis,
  FLOOR_VIEW_SCALE_BOUNDS,
  type EdgeDimension,
} from './floorPolygon';

export {
  CAD_VIEW_IDENTITY,
  CAD_WHEEL_ZOOM_SENSITIVITY,
  wheelZoomFactor,
  cadViewToScreen,
  screenToCadView,
  zoomCadViewAt,
  panCadView,
  isPointInView,
  type CadView2D,
  type CadScaleBounds,
} from './cadView';

export {
  computeFloorplanView,
  screenToWorld,
  worldToScreen,
  clampToRoom,
  withoutHiddenElements,
  type FloorplanView,
} from './view';

export {
  type CeilingAxis,
  type RoofPreset,
  type CeilingViewGeom,
  ROOF_PRESETS,
  axisProfileFlat,
  MIN_CEILING_NODE_M,
  ceilingZRange,
  resolvePrimaryAxis,
  normalizeCeiling,
  applyCeilingPreset,
  matchedPreset,
  activeCeilingNodes,
  setAxisNodes,
  ceilingViewGeom,
  ceilingNodeToPx,
  ceilingPxToNode,
  hitTestCeilingNode,
  moveCeilingNode,
  addCeilingNode,
  removeCeilingNode,
  type CeilingView,
  CEILING_VIEW_FIT,
  CEILING_VIEW_SCALE_BOUNDS,
  MIN_CEILING_VIEW_SCALE,
  MAX_CEILING_VIEW_SCALE,
  ceilingFitToScreen,
  ceilingScreenToFit,
  ceilingNodeToScreen,
  ceilingScreenToNode,
  computeCeilingHeightDimensions,
  type CeilingHeightDimension,
} from './ceiling';

export {
  type RoomBBox,
  ceilingHasSlope,
  ceilingHeightAt,
  ceilingIsSmooth,
  profileBreakUs,
} from './ceilingWorld';

export {
  NOGO_MIN_SIZE_M,
  genNoGoId,
  normRect,
  rectMeetsMinSize,
  clampRectToRoom,
  pointInNoGoZone,
  hitTestNoGoZone,
  draftToNoGoZone,
  defaultCornerNoGoZone,
  snapYaw,
  roomFloorPolygon,
  pointInPolygon,
  intersectRectPolygon,
} from './nogoEdit';

export {
  type RoomMode,
  calculateRoomModes,
  calculateSchroederFrequency,
  estimateRt60Seconds,
} from './roomModes';

export {
  type StereoGeometry,
  type ReflectionPoint,
  type AxialExposure,
  stereoGeometry,
  firstReflectionPoints,
  axialModeExposure,
} from './listening';

export {
  type SnapSourceKind,
  type SnapSource,
  type SnapSpan,
  type SnapCandidate,
  type SnapResult,
  type AxisFootprint,
  alignmentCandidates,
  axisCandidates,
  betweenCenterCandidates,
  dedupeCandidates,
  equalSpacingCandidates,
  gapMirrorCandidates,
  nearestAngleCandidate,
  nearestSnapCandidate,
  pixelRadiusToWorldThreshold,
  sameCandidate,
  stickyNearestSnapCandidate,
  DEDUPE_EPSILON_M,
  SNAP_STICKINESS,
} from './snapEngine';

export {
  type AbsorberSpec,
  type AbsorptionCurve,
  type Incidence,
  type MaterialPreset,
  ABS_F_MIN,
  ABS_F_MAX,
  absorptionAt,
  absorptionCurve,
  nrc,
  MATERIAL_PRESETS,
} from './porousAbsorber';
